import React, { useEffect, useMemo } from 'react'
import {
  Box,
  Grid,
  Typography,
  Divider,
  Alert,
  Chip,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import BusinessIcon from '@mui/icons-material/Business'
import PaletteIcon from '@mui/icons-material/Palette'
import ImageIcon from '@mui/icons-material/Image'
import InfoIcon from '@mui/icons-material/Info'

import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import { AppearanceConfig, BrandingConfig } from '../types/EnvironmentConfig.types'
import { useWorkspaceBranding } from '../hooks/useWorkspaceBranding'
import { ColorPicker } from '../components/ColorPicker'
import ThemePreviewPanel from '../components/ThemePreviewPanel'
import type { SevvThemeTokens } from '@/theme/sevvThemeTokenModel'
import {
  environmentConfigSectionDividerSx,
  environmentConfigSectionRootSx,
} from '../utils/environmentConfigChrome'

interface BrandingSettingsProps {
  config: BrandingConfig
  onChange: (updates: Partial<BrandingConfig>) => void
  appearance: AppearanceConfig
  designTokens: SevvThemeTokens
  disabled?: boolean
}

/**
 * Branding Settings Section
 * Manages platform branding, logos, colors, and authentication dialog appearance
 */
const BrandingSettings: React.FC<BrandingSettingsProps> = ({
  config,
  onChange,
  appearance,
  designTokens,
  disabled = false,
}) => {
  const theme = useTheme()
  const { branding, loading: brandingLoading } = useWorkspaceBranding()

  const brandingPreview = useMemo(
    () => ({
      platformName: config.platformName,
      logoUrl: config.logoUrl,
      authDialogPlatformName: config.authDialogPlatformName,
      authDialogLogoUrl: config.authDialogLogoUrl,
      logoAltText: config.logoAltText,
    }),
    [
      config.platformName,
      config.logoUrl,
      config.authDialogPlatformName,
      config.authDialogLogoUrl,
      config.logoAltText,
    ]
  )

  const handleChange = (field: keyof BrandingConfig, value: any) => {
    console.log(`[BrandingSettings] Updating ${field}:`, value)
    onChange({ [field]: value })
  }

  // Debug: Log when branding config changes
  useEffect(() => {
    console.log('[BrandingSettings] Branding config updated:', config)
  }, [config])

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
      {/* Current Branding Info */}
      {branding && !brandingLoading && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mb: 3, 
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: theme.shadows[2],
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            '& .MuiAlert-icon': {
              color: 'primary.main',
            }
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 1, 
              color: 'text.primary',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              fontWeight: 700
            }}
          >
            Current Platform Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Chip 
              label={`Platform: ${branding.platformName}`} 
              size="small" 
              variant="outlined"
              sx={{ 
                borderColor: 'divider', 
                color: 'text.primary',
                backgroundColor: theme.palette.action.hover,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            />
            <Chip 
              label={`URL: ${branding.platformUrl}`} 
              size="small" 
              variant="outlined"
              sx={{ 
                borderColor: 'divider', 
                color: 'text.primary',
                backgroundColor: theme.palette.action.hover,
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            />
            <Chip 
              label={`Primary Color: ${branding.primaryColor}`} 
              size="small" 
              variant="outlined"
              sx={{ 
                borderColor: branding.primaryColor, 
                color: 'text.primary',
                backgroundColor: alpha(branding.primaryColor, 0.08),
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                fontWeight: 500,
                '&:hover': {
                  borderColor: branding.primaryColor,
                  backgroundColor: alpha(branding.primaryColor, 0.15),
                }
              }}
            />
          </Box>
        </Alert>
      )}

      {/* Platform Branding */}
      <ConfigSection
        title="Platform Branding"
        description="Configure the main platform branding and identity"
        icon={<BusinessIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Platform Name"
              value={config.platformName}
              onChange={(value) => handleChange('platformName', value)}
              type="text"
              placeholder="Sevv"
              disabled={disabled}
              helpText="The name displayed throughout the platform"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Platform URL"
              value={config.platformUrl}
              onChange={(value) => handleChange('platformUrl', value)}
              type="url"
              placeholder="https://app.sevv.com"
              disabled={disabled}
              helpText="The main website URL for your platform"
            />
          </Grid>
          
          <Grid item xs={12}>
            <ConfigField
              label="Platform Description"
              value={config.platformDescription || ''}
              onChange={(value) => handleChange('platformDescription', value)}
              type="text"
              placeholder="A modern virtual office platform for remote teams"
              disabled={disabled}
              helpText="Brief description of your platform"
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Logo and Favicon */}
      <ConfigSection
        title="Logo and Favicon"
        description="Configure your platform's logo and favicon"
        icon={<ImageIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Logo URL"
              value={config.logoUrl}
              onChange={(value) => handleChange('logoUrl', value)}
              type="url"
              placeholder="https://your-domain.com/logo.png"
              disabled={disabled}
              helpText="URL to your platform's logo image (displayed in headers and dialogs)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Favicon URL"
              value={config.faviconUrl || ''}
              onChange={(value) => handleChange('faviconUrl', value)}
              type="url"
              placeholder="https://your-domain.com/favicon.ico"
              disabled={disabled}
              helpText="URL to your platform's favicon (displayed in browser tabs)"
            />
          </Grid>
          
          <Grid item xs={12}>
            <ConfigField
              label="Logo Alt Text"
              value={config.logoAltText || ''}
              onChange={(value) => handleChange('logoAltText', value)}
              type="text"
              placeholder="Platform Logo"
              disabled={disabled}
              helpText="Alternative text for accessibility (optional)"
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Brand Colors */}
      <ConfigSection
        title="Brand Colors"
        description="Configure brand colors used throughout the platform"
        icon={<PaletteIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <ColorPicker
                label="Primary Color"
                value={config.primaryColor}
                onChange={(value) => handleChange('primaryColor', value)}
                presetColors={[
                  '#f97415', // Current brand orange
                  '#2196f3', // Material blue
                  '#3f51b5', // Indigo
                  '#1976d2', // Dark blue
                  '#0d47a1', // Deep blue
                  '#1565c0', // Blue 800
                  '#42a5f5', // Light blue
                  '#90caf9', // Blue 200
                ]}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Primary brand color used in buttons and accents
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <ColorPicker
                label="Secondary Color"
                value={config.secondaryColor}
                onChange={(value) => handleChange('secondaryColor', value)}
                presetColors={[
                  '#2c3e50', // Current brand dark
                  '#424242', // Grey 800
                  '#616161', // Grey 700
                  '#757575', // Grey 600
                  '#9e9e9e', // Grey 500
                  '#bdbdbd', // Grey 400
                  '#e0e0e0', // Grey 300
                  '#f5f5f5', // Grey 100
                ]}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Secondary brand color for accents and highlights
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <ColorPicker
                label="Accent Color"
                value={config.accentColor || '#e74c3c'}
                onChange={(value) => handleChange('accentColor', value)}
                presetColors={[
                  '#e74c3c', // Red accent
                  '#f44336', // Material red
                  '#ff5722', // Deep orange
                  '#ff9800', // Orange
                  '#ffc107', // Amber
                  '#ffeb3b', // Yellow
                  '#cddc39', // Lime
                  '#8bc34a', // Light green
                ]}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Accent color for highlights and alerts
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <ColorPicker
                label="Text Color"
                value={config.textColor || '#212529'}
                onChange={(value) => handleChange('textColor', value)}
                presetColors={[
                  '#212529', // Dark text
                  '#000000', // Black
                  '#2c3e50', // Dark blue-grey
                  '#34495e', // Dark slate
                  '#495057', // Dark grey
                  '#6c757d', // Medium grey
                  '#adb5bd', // Light grey
                  '#ffffff', // White
                ]}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Primary text color
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <ColorPicker
                label="Background Color"
                value={config.backgroundColor || '#ffffff'}
                onChange={(value) => handleChange('backgroundColor', value)}
                presetColors={[
                  '#ffffff', // White
                  '#f8f9fa', // Light grey
                  '#e9ecef', // Grey 200
                  '#dee2e6', // Grey 300
                  '#ced4da', // Grey 400
                  '#adb5bd', // Grey 500
                  '#6c757d', // Grey 600
                  '#495057', // Grey 700
                ]}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Main background color
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Authentication Dialog Branding */}
      <ConfigSection
        title="Authentication Dialog Branding"
        description="Configure the appearance of the login and registration dialog that users see when they are not authenticated"
        icon={<BusinessIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Dialog Platform Name"
              value={config.authDialogPlatformName || config.platformName}
              onChange={(value) => handleChange('authDialogPlatformName', value)}
              type="text"
              placeholder="Sevv"
              disabled={disabled}
              helpText="The name displayed in the authentication dialog (defaults to platform name)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Dialog Logo URL"
              value={config.authDialogLogoUrl || config.logoUrl}
              onChange={(value) => handleChange('authDialogLogoUrl', value)}
              type="url"
              placeholder="https://your-domain.com/auth-logo.png"
              disabled={disabled}
              helpText="Logo specifically for the authentication dialog (defaults to main logo)"
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Preview Information */}
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ 
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          color: 'success.main',
          '& .MuiAlert-icon': {
            color: 'success.main',
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
          Preview Information:
        </Typography>
        <Typography variant="body2" sx={{ color: 'success.main' }}>
          These branding settings affect the appearance throughout the platform. 
          Changes will be visible to all users after saving. 
          The authentication dialog branding will be applied immediately to users who are not yet authenticated.
        </Typography>
      </Alert>
        </Grid>
        <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              position: { md: 'sticky' },
              top: { md: 16 },
              alignSelf: 'flex-start',
            }}
          >
            <ThemePreviewPanel
              appearance={appearance}
              designTokens={designTokens}
              brandingPreview={brandingPreview}
              variant="compact"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default BrandingSettings 